import * as React from "react";
import { PropertyDialog } from "../framework/dialog";
import { Base } from "../../framework/base";
import { SaveData } from "../../framework/saveData";
import { ConfirmationDialogType } from "../../models/store/storeTypes";
import * as store from "../../framework/customStore";
import * as baseService from "../../services/baseService";
import * as storeActions from "../../models/store/storeActions";
import { IVehicleGroupEdit } from "../../models/vehicleGroup/vehicleGroupEdit";
import * as vehicleGroupService from "../../services/vehicleGroupService";
import { VehicleItem } from "../../models/vehicle/vehicleItem";
import { t } from "i18next";
import { AutocompleteOption, AutocompleteWrapper } from "../framework/muiAutocomplete";
import { getSalaryRowTypes } from "../../services/workTimeBetaService";
import { handleApiError } from "../../models/store/storeEffects";
import { ISalaryRowType } from "../../models/workShitTimeSlot/salaryRowType";
import { CheckBox } from "../framework/checkbox";

export interface ISettingsVehicleGroupDialogProps {
    classes?: string;
    editItem: IVehicleGroupEdit;
    onOk: () => void;
    onCancel: () => void;
}

export interface ISettingsVehicleGroupDialogState {
    vehicles: VehicleItem[];
    vehicleGroupId: string;
    name: string;
    description: string;
    salaryRowTypeIds: string[];
    allSalaryRowTypes: ISalaryRowType[];
    isDefaultNewIntegrationVehicleGroup: boolean;
    isInitiallyDefaultNewIntegrationVehicleGroup : boolean;
}

export class SettingsVehicleGroupDialog extends React.Component<ISettingsVehicleGroupDialogProps, ISettingsVehicleGroupDialogState> {
    private static orgStateHash: string = "";

    constructor(props: ISettingsVehicleGroupDialogProps) {
        super(props);
        const vehicleGroup = props.editItem.vehicleGroup;
        const vehicles = props.editItem.vehicles;

        this.state = {
            vehicles: vehicles,
            vehicleGroupId: vehicleGroup.id,
            name: vehicleGroup.name,
            description: vehicleGroup.description,
            salaryRowTypeIds: vehicleGroup.salaryRowTypeIds,
            allSalaryRowTypes: [],
            isDefaultNewIntegrationVehicleGroup: vehicleGroup.isDefaultNewIntegrationVehicleGroup,
            isInitiallyDefaultNewIntegrationVehicleGroup: vehicleGroup.isDefaultNewIntegrationVehicleGroup
        };
        const saveData = SettingsVehicleGroupDialog.getSaveDataFromState(props, this.state);
        SettingsVehicleGroupDialog.orgStateHash = saveData.hash;

        this.loadSalaryRowTypes();
    }

    handleChange = (event) => {
        const target = event.target;
        const value: string = target.value;
        const name: string = target.name;

        if (name === "nm") {
            this.setState({ name: value });
        } else if (name === "description") {
            this.setState({ description: value });
        }
    };

    handleChangeDefaultIntegrationVehicleGroup = (checked :boolean) => {
        this.setState({ isDefaultNewIntegrationVehicleGroup: checked });
    };

    async loadSalaryRowTypes() {
        getSalaryRowTypes().then(
            (result) => {
                this.setState({ allSalaryRowTypes: result });
            },
            (error) => {
                handleApiError(error, store.customStore.dispatch);
            }
        );
    }

    private static validate = (state: ISettingsVehicleGroupDialogState): boolean => {
        if (!state.name) {
            store.customStore.dispatch(storeActions.showErrorMessage(t("common.nameMustBeDefined")));
            return false;
        }
        return true;
    };

    private static getSaveDataFromState = (props: ISettingsVehicleGroupDialogProps, state: ISettingsVehicleGroupDialogState): SaveData => {
        const data = new SaveData();
        const vehicleGroup = props.editItem.vehicleGroup;

        data.append("id", vehicleGroup.id);
        data.append("rowId", vehicleGroup.rowId);
        data.append("name", state.name);
        data.append("description", state.description);
        data.append("salaryRowTypeIds", JSON.stringify(state.salaryRowTypeIds.map(i => i)));
        data.append("isDefaultNewIntegrationVehicleGroup", JSON.stringify(state.isDefaultNewIntegrationVehicleGroup));
        return data;
    };

    saveEditItem = () => {
        const obj = this;
        if (!SettingsVehicleGroupDialog.validate(this.state)) return;

        const saveData = SettingsVehicleGroupDialog.getSaveDataFromState(this.props, this.state);
        if (!saveData) return;

        store.customStore.dispatch(storeActions.fetchStart());
        vehicleGroupService.saveVehicleGroupEdit(saveData.formData)
            .then(success => {
                store.customStore.dispatch(storeActions.showSuccessMessage(success.message));
                obj.props.onOk();
            })
            .catch(error => {
                store.customStore.dispatch(storeActions.showErrorMessage(baseService.getErrorMessageFromError(error)));
                return null;
            })
            .finally(() => store.customStore.dispatch(storeActions.fetchEnd()));
    };

    handleOkClick = () => {
        this.saveEditItem();
    };

    handleCancelClick = () => {
        const obj = this;
        const saveData = SettingsVehicleGroupDialog.getSaveDataFromState(this.props, this.state);
        if (!Base.isNullOrUndefined(saveData) && saveData.hash !== SettingsVehicleGroupDialog.orgStateHash) {
            store.customStore.dispatch(storeActions.setConfirmation(ConfirmationDialogType.Warning, t("common.warning"), t("common.youHaveNotSavedChangesDoYouWantToSaveChanges"),
                () => {
                    store.customStore.dispatch(storeActions.clearConfirmation());
                    obj.saveEditItem();
                },
                () => {
                    store.customStore.dispatch(storeActions.clearConfirmation());
                    obj.props.onCancel();
                },
                () => {
                    store.customStore.dispatch(storeActions.clearConfirmation());
                }));
        } else {
            obj.props.onCancel();
        }
    };

    handleHourBookingCategoryChange = (val: AutocompleteOption[]) => {
        this.setState({ salaryRowTypeIds: val.map(v => v.id) });
    };

    render() {
        const props = this.props;
        const state = this.state;
        const dialogClasses = "settings VehicleGroup px800" + (props.classes ? " " + props.classes : "");

        return (
            <div>
                <PropertyDialog
                    classes={dialogClasses}
                    title={t("vehicleGroupSettings.vehicleGroupNewTitle")}
                    show={true}
                    body={
                        <div className="">
                            <div>
                                <div className="row">
                                    <div className="col-12">
                                        <div className="form-group required">
                                            <label className="control-label smallFont">{t("common.name")}</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="nm"
                                                title={t("common.name")}
                                                value={state.name}
                                                onChange={this.handleChange}
                                                maxLength={50}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col-12">
                                        <div className="form-group">
                                            <label className="control-label smallFont">{t("common.description")}</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="description"
                                                title={t("common.description")}
                                                value={state.description}
                                                onChange={this.handleChange}
                                                maxLength={1000}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col-12">
                                        <div className="form-group mt-2">
                                            <AutocompleteWrapper
                                                label={t("fleetAdditions")}
                                                value={state.salaryRowTypeIds || []}
                                                multiple
                                                fullWidth
                                                size="small"
                                                options={state.allSalaryRowTypes?.map((s) =>
                                                    ({ title: s.name, id: s.id })
                                                )}
                                                onChange={this.handleHourBookingCategoryChange}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col-12">
                                        <div className="form-group">
                                            <label className="control-label smallFont">&nbsp;</label>
                                            <div>
                                                <CheckBox
                                                    title={t("vehicleGroupSettings.defaultNewIntegrationVehicleGroup")}
                                                    enabled={!state.isInitiallyDefaultNewIntegrationVehicleGroup}
                                                    checked={state.isDefaultNewIntegrationVehicleGroup}
                                                    onCheckboxClickBoolean={(checked: boolean) => {
                                                        this.handleChangeDefaultIntegrationVehicleGroup(checked);}}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    }
                    buttons={[
                        { title: t("common.save"), classes: "btn-primary", enabled: true, onClick: this.handleOkClick },
                    ]}
                    onClose={this.handleCancelClick}
                />
            </div>
        );
    }
}
